import type { ImagePaint, Paint } from '@flobi/artwork-core';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Button, NumberField, SegmentedControl } from '../../ui/controls';
import type { LibraryPicture } from '../useEditorCanvas';
import { tileUrl } from '../content/tileUrl';

export interface PaintImages {
  enabled: boolean;
  pictures(): LibraryPicture[];
  videoPictures?(): LibraryPicture[];
  upload(file: File): Promise<LibraryPicture | null>;
}

export const PaintImagesContext = createContext<PaintImages | null>(null);
export const usePaintImages = () => useContext(PaintImagesContext);

/** Uploads an asset for a paint, without adding an image layer to the page. */
export function ImageFillPicker({ paint, onChange }: { paint: Paint; onChange(paint: Paint): void }) {
  const images = usePaintImages()!;
  const input = useRef<HTMLInputElement>(null);
  const alive = useRef(true);
  const latest = useRef({ paint, onChange });
  latest.current = { paint, onChange };
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);
  const pictures = images.pictures();
  const image = paint.type === 'image' ? paint : null;
  const alternatives = [...pictures, ...(images.videoPictures?.() || [])];
  const current = image ? alternatives.find((one) => one.assetId === image.assetId) : undefined;
  const choose = (picture: LibraryPicture) => {
    const { paint, onChange } = latest.current;
    const image = paint.type === 'image' ? paint : null;
    const next: ImagePaint = {
      type: 'image', assetId: picture.assetId, fit: image?.fit === 'fit' ? 'fit' : 'crop', scale: 1,
      visible: true, blendMode: paint.blendMode ?? 'normal',
      opacity: (paint.opacity ?? 1) * (paint.type === 'solid' ? paint.color.a ?? 1 : 1),
    };
    setError(null);
    onChange(next);
  };
  const upload = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      const picture = await images.upload(file);
      if (alive.current && picture) choose(picture);
    } catch (problem) {
      if (alive.current) setError(problem instanceof Error ? problem.message : 'Could not upload the image. Try another file.');
    } finally {
      if (alive.current) setBusy(false);
    }
  };
  return <div className="grid min-w-0 gap-[10px]">
    <input ref={input} type="file" accept="image/*" className="hidden" aria-label="Choose fill image" onChange={(event) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (file) void upload(file);
    }} />
    {current ? <div className="flex h-[144px] items-center justify-center overflow-hidden rounded-[10px] bg-fill-1">
      {current.duration ? <video src={current.src} controls muted className="h-full w-full" style={{objectFit:image?.fit==='fit'?'contain':'cover'}} /> : <img src={tileUrl(current.src, 720)} alt={current.name} className="h-full w-full" style={{ objectFit: image?.fit === 'fit' ? 'contain' : 'cover' }} onError={() => setError('The image could not be loaded. Replace it or choose another from this file.')} />}
    </div> : null}
    <Button variant="prominent" disabled={busy} onClick={() => input.current?.click()}>
      {busy ? 'Uploading…' : image ? 'Replace image' : 'Choose image'}
    </Button>
    {error ? <p role="alert" className="text-[12px] text-label-2">{error}</p> : null}
    {image ? <div className="flex items-center gap-[8px]">
      <SegmentedControl label="Image sizing" size="small" value={image.fit === 'fit' ? 'fit' : 'crop'}
        onChange={(fit) => onChange({ ...image, fit, transform: undefined })}
        segments={[{ value: 'crop', label: 'Fill', tip: 'Cover the shape, cropping the image' }, { value: 'fit', label: 'Fit', tip: 'Show the whole image inside the shape' }]}
        className="min-w-0 flex-1" />
      <NumberField label="Image opacity" value={Math.round((image.opacity ?? 1) * 100)} min={0} max={100} decimals={0} suffix="%"
        onChange={(value) => onChange({ ...image, opacity: value / 100 })} className="w-[80px]" />
    </div> : null}
    {pictures.length ? <>
      <span className="text-[11px] font-medium text-label-2">In this file</span>
      {/* The scroll is a box round the grid: a height-capped grid squeezes its rows, and the tiles stacked. */}
      <div className="ui-scroll max-h-[156px] overflow-y-auto"><div className="grid grid-cols-4 gap-[6px]">
        {pictures.map((picture) => <button key={picture.assetId} type="button" disabled={busy} aria-label={`Use ${picture.name} as fill`}
          aria-pressed={image?.assetId === picture.assetId} title={picture.name} onClick={() => choose(picture)}
          className={`relative aspect-square w-full overflow-hidden rounded-[7px] bg-fill-1 ${image?.assetId === picture.assetId ? 'ring-2 ring-inset ring-accent' : ''}`}>
          <img src={tileUrl(picture.src)} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
        </button>)}
      </div></div>
    </> : null}
  </div>;
}

/** A fill well uses the document image's thumbnail rather than an empty checkerboard. */
/** A video fill's clip, for a swatch that shows its first frame (a CSS background cannot). */
export function videoSwatch(paint: Paint, images: PaintImages | null): string | undefined {
  if (paint.type !== 'image') return undefined;
  return images?.videoPictures?.().find((one) => one.assetId === paint.assetId)?.src;
}

export function imageSwatch(paint: Paint, images: PaintImages | null): string | undefined {
  if (paint.type !== 'image') return undefined;
  const picture = images?.pictures().find((one) => one.assetId === paint.assetId);
  return picture ? `url(${JSON.stringify(tileUrl(picture.src))}) center / cover` : undefined;
}
